import constants from 'constants/constants';
import moment from 'moment-timezone';
import { isStringHasRealContent } from 'utilitys/helpers/general';

import { QueryResponse as CardResponse } from 'api/queries/getMainCard.query';

import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ProfileImage } from 'components/common/ProfileImage/profileImage';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/comments.module.scss';
import { CommentsAiMenu } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/commentsAiMenu';
import { useCommentProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/useCommentProvider';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedConfirmDeleteModal, CustomizedRoundIcon } from 'components/common';

interface ICommentsProps {
    id: number;
    isVirtual: boolean;
    assigneeOptions: { text: string; value: string; url: string }[];
    card?: CardResponse;
    queryKeys?: string[];
    enableQuickComments?: boolean;
}

export const CommentsDesktop = ({
    id,
    isVirtual,
    assigneeOptions,
    card,
    queryKeys = [],
    enableQuickComments = true,
}: ICommentsProps) => {
    const {
        cancelAddCommentMode,
        commentData,
        commentError,
        commentId,
        commentsEditorStatus,
        confirmDeleteComment,
        createComment,
        deleteCommentModalShow,
        email,
        firstName,
        handleCommentDelete,
        handleEditorDescription,
        image,
        isAIMitigationPrivilege,
        isLoading,
        isLoadingMitigation,
        lastName,
        removeCommentId,
        resetCommentsEditorStatus,
        setAddCommentMode,
        setCommentData,
        setCommentError,
        setCommentId,
        setDeleteCommentModalShow,
        setIsLoadingMitigation,
        sortedComments,
        updateComment,
        username,
    } = useCommentProvider({ id, isVirtual, queryKeys });

    return (
        <div className={classes.commentsContainer} data-track-id={`comments-card`}>
            {isLoading && <OverlayWithSpinner />}
            <div className={classes.titleWrapper}>
                <div className={classes.leftSection}>
                    <div className={classes.title}>Comments:</div>
                    {isAIMitigationPrivilege && card && (
                        <CommentsAiMenu
                            isLoadingMitigation={isLoadingMitigation}
                            setIsLoadingMitigation={setIsLoadingMitigation}
                            card={card}
                        />
                    )}
                </div>
                {commentsEditorStatus.isOpen && (
                    <div className={classes.actionButtons}>
                        <Separator />
                        <Button variant={'secondary'} onClick={cancelAddCommentMode}>
                            Cancel
                        </Button>
                        <Button variant={'primary'} onClick={createComment}>
                            Add
                        </Button>
                    </div>
                )}
            </div>
            <div className={classes.addCommentContainer}>
                <div className={classes.profileImageWrapper}>
                    <ProfileImage fullName={`${firstName} ${lastName}`} email={email} imageName={image || ''} />
                </div>
                {!commentsEditorStatus.isOpen ? (
                    <div className={classes.addCommentAndQuickCommentsContainer}>
                        <button
                            data-testid={'add-comment'}
                            className={classes.addCommentInput}
                            onClick={() => setAddCommentMode('')}
                        >
                            Add a comment...
                        </button>

                        {enableQuickComments && (
                            <div className={classes.quickCommentsContainer}>
                                <Button
                                    className={classes.quickCommentText}
                                    variant={'secondary'}
                                    onClick={() => setAddCommentMode('Activity completed')}
                                    size={'small'}
                                >
                                    ✅ Activity completed
                                </Button>
                                <Button
                                    className={classes.quickCommentText}
                                    variant={'secondary'}
                                    onClick={() => setAddCommentMode('We’re on track')}
                                    size={'small'}
                                >
                                    👍 We’re on track
                                </Button>
                                <Button
                                    className={classes.quickCommentText}
                                    variant={'secondary'}
                                    onClick={() => setAddCommentMode('Waiting on an update')}
                                    size={'small'}
                                >
                                    ⌛️ Waiting on an update
                                </Button>
                                <Button
                                    className={classes.quickCommentText}
                                    variant={'secondary'}
                                    onClick={() => setAddCommentMode('Update status')}
                                    size={'small'}
                                >
                                    📢 Update status
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={classes.addCommentWrapper}>
                        <div data-testid={'comments-text-area'} className={classes.textArea}>
                            <RichTextArea
                                onEditorDescription={handleEditorDescription}
                                existingData={commentData.comment}
                                editorContentHeight={180}
                                isAutoFocus={true}
                                isError={commentError}
                                mention={{
                                    separator: ' ',
                                    trigger: '@',
                                    suggestions: assigneeOptions,
                                }}
                                placeholder={
                                    'Enter your comments here. \nType @ to mention another user and notify them about it.'
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            {sortedComments.map((item) => (
                <div key={item.updatedDate} className={classes.commentWrapper}>
                    <ProfileImage
                        fullName={`${item.assignee.firstName} ${item.assignee.lastName}`}
                        email={item.assignee.userName}
                        imageName={item.assignee.imageName || ''}
                    />
                    <div data-testid={'comments-row'} className={classes.wrapper}>
                        <div className={classes.iconsWrapper}>
                            <div className={classes.iconsHeader}>
                                <span>{`${item.firstName} ${item.lastName}`}</span>
                                <span>
                                    {moment(item.updatedDate).format(constants.formats.date.fullDateWithMilliSeconds)}
                                </span>

                                {username === item.userName && (
                                    <div className={classes.actionIcons}>
                                        <CustomizedRoundIcon
                                            size={'26px'}
                                            onClick={() => {
                                                setCommentId(item.commentId);
                                                setCommentError(false);
                                                setCommentData({ ...commentData, id: item.commentId, comment: '' });
                                                resetCommentsEditorStatus();
                                            }}
                                        >
                                            <Icon name={'edit-user-profile'} size={'16px'} />
                                        </CustomizedRoundIcon>
                                        <CustomizedRoundIcon
                                            size={'26px'}
                                            onClick={() => {
                                                confirmDeleteComment(item.commentId);
                                            }}
                                        >
                                            <MuiIcon
                                                data-testid={'delete-icon'}
                                                icon={'delete'}
                                                type={'outlined'}
                                                fontSize={'16px'}
                                            />
                                        </CustomizedRoundIcon>
                                    </div>
                                )}
                            </div>

                            {commentId === item.commentId && (
                                <div className={classes.actionButtons}>
                                    <Button
                                        variant={'secondary'}
                                        onClick={() => {
                                            setCommentId(false);
                                            setCommentData({ ...commentData, id: null, comment: '' });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant={'primary'}
                                        onClick={() => {
                                            updateComment(item.commentId);
                                        }}
                                    >
                                        Update
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div data-testid={'comment-text'} className={classes.commentText}>
                            {commentId === item.commentId ? (
                                <div className={classes.textArea}>
                                    <RichTextArea
                                        onEditorDescription={handleEditorDescription}
                                        existingData={
                                            commentData.id === item.commentId &&
                                            !isStringHasRealContent(commentData.comment)
                                                ? commentData.comment
                                                : item.comment
                                        }
                                        editorContentHeight={160}
                                        isAutoFocus={true}
                                        isError={commentError}
                                        mention={{
                                            separator: ' ',
                                            trigger: '@',
                                            suggestions: assigneeOptions,
                                        }}
                                    />
                                </div>
                            ) : (
                                <span dangerouslySetInnerHTML={{ __html: item.comment }} />
                            )}
                        </div>
                    </div>
                </div>
            ))}

            {removeCommentId && (
                <CustomizedConfirmDeleteModal
                    isShowModal={deleteCommentModalShow}
                    hideModal={() => {
                        setDeleteCommentModalShow(false);
                    }}
                    text={'Are you sure you want to delete this Comment?'}
                    onConfirm={() => handleCommentDelete(removeCommentId)}
                />
            )}
        </div>
    );
};
